import { Entity, Column, PrimaryColumn, ManyToOne } from 'typeorm';
import { FormatDate } from '../../_objects/format-date';
import {
  Inspection,
  InspectionDetailNew,
  InspectionDetailUpdate,
  InspectionUpdateLocation,
  Inspector
} from '../../_objects/inspection';
import { SubjectFilter } from '../../_services/storage.service';
import { BaseSchema } from '../abstracts/base.schema';
import { TenantIdSchemaBase } from '../interfaces/tenantId.base';
import { SubjectSchema } from './subject.schema';

@Entity()
export class InspectionSchema extends BaseSchema implements TenantIdSchemaBase {
  @PrimaryColumn()
    id: string;

  @Column({ length: 100 })
    title: string;

  @Column()
    subjectId: string;

  @Column()
    inspectionDate: string;

  @Column()
    status: string;

  @Column({ nullable: true })
    inspectors: string | null;

  @Column()
    isValid: boolean;

  @Column({ nullable: true })
    schemaVersion: string | null;

  @Column()
    tenantId: string;

  @Column()
    isDeletedFlag: boolean;

  @Column({ nullable: true })
    createDate: string | null;

  @Column({ nullable: true })
    lastUpdateDate: string | null;

  @Column({ nullable: true })
    rowVersion: string | null;

  @Column({ nullable: true })
    inspectionLocations: string | null;

  @Column({ nullable: true })
    sharedLockBy: string | null;

  @Column({ nullable: true })
    imageCount: number | null;
    
  @Column({ nullable: true })
    reportCount: number | null;

  @Column()
    isOfflineData: boolean;

  @Column({ nullable: true })
    manageUserId: string | null;

  @Column({ nullable: true })
    locationVersion: number | null;

  @Column({ nullable: true })
    attributesVersion: number | null;

  @Column({ nullable: true })
    connectorTenantId: string | null;

  // relations
  @ManyToOne('SubjectSchema', 'inspections')
    subject: SubjectSchema;

  setInspection(tenantId: string, inspection: Inspection, isOffline: boolean, userId: string) {
    this.tenantId = tenantId;
    this.id = inspection.id;
    this.title = inspection.title;
    this.subjectId = inspection.subjectId;
    this.inspectionDate = inspection.inspectionDate;
    this.status = inspection.status;
    this.inspectors = JSON.stringify(inspection.inspectors);
    this.isValid = inspection.isValid;
    this.createdBy = inspection.createdBy;
    this.createDate = inspection.createDate;
    this.lastUpdatedBy = inspection.lastUpdatedBy;
    this.lastUpdateDate = inspection.lastUpdateDate;
    this.inspectionLocations = JSON.stringify(inspection.inspectionLocations);
    this.isDeletedFlag = false;
    this.schemaVersion = inspection.schemaVersion;
    this.sharedLockBy = inspection.sharedLockBy;
    this.isOfflineData = isOffline;
    this.manageUserId = userId;
    this.locationVersion = inspection.locationVersion;
    this.imageCount = inspection.imageCount;
    this.connectorTenantId = inspection.connectorTenantId;
    this.reportCount = inspection.reportCount;
  }

  setInspectionDetailNew(tenantId: string, inspection: InspectionDetailNew,
    isOffline: boolean, id: string, userId: string, locationVersion: number, attributesVersion: number) {
    const date = new FormatDate();
    this.tenantId = tenantId;
    this.id = id;
    this.title = inspection.title;
    this.subjectId = inspection.subjectId;
    this.inspectionDate = inspection.inspectionDate;
    this.status = inspection.status;
    inspection.inspectors.map(user => {
      const result = new Inspector();
      result.id = user.id;
      return result;
    });
    this.inspectors = JSON.stringify(inspection.inspectors);

    this.isValid = true;
    this.createdBy = userId;
    this.createDate = date.toString();
    this.lastUpdatedBy = userId;
    this.lastUpdateDate = date.toString();
    this.rowVersion = null;
    this.inspectionLocations = JSON.stringify([]);
    this.isDeletedFlag = false;
    this.schemaVersion = inspection.schemaVersion;
    this.isOfflineData = isOffline;
    this.manageUserId = userId;
    this.locationVersion = locationVersion;
    this.attributesVersion = attributesVersion;
    this.imageCount = inspection.imageCount;
    this.connectorTenantId = inspection.connectorTenantId;
    this.reportCount = inspection.reportCount;
  }
}

export class InspectionSchemaUpdate implements TenantIdSchemaBase {
  id: string;
  title: string;
  subjectId: string;
  inspectionDate: string;
  status: string;
  inspectors: string;
  isValid: boolean;
  tenantId: string;
  schemaVersion: string;
  lastUpdateDate: string;
  lastUpdatedBy: string;
  rowVersion: string;
  locationVersion: number;
  imageCount: number;
  connectorTenantId: string | null;
  reportCount: number;

  setInspectionDetailUpdate(tenantId: string, inspection: InspectionDetailUpdate, userId: string) {
    const date = new FormatDate();
    this.tenantId = tenantId;
    this.id = inspection.id;
    this.title = inspection.title;
    this.subjectId = inspection.subjectId;
    this.inspectionDate = inspection.inspectionDate;
    this.status = inspection.status;
    this.inspectors = JSON.stringify(inspection.inspectors);
    this.isValid = inspection.isValid;
    this.lastUpdatedBy = userId;
    this.lastUpdateDate = date.toString();
    this.schemaVersion = inspection.schemaVersion;
    this.locationVersion = inspection.locationVersion;
    this.imageCount = inspection.imageCount;
    this.connectorTenantId = inspection.connectorTenantId;
    this.reportCount = inspection.reportCount;
  }

  setInspection(tenantId: string, inspection: Inspection) {
    this.tenantId = tenantId;
    this.id = inspection.id;
    this.title = inspection.title;
    this.subjectId = inspection.subjectId;
    this.inspectionDate = inspection.inspectionDate;
    this.status = inspection.status;
    this.inspectors = JSON.stringify(inspection.inspectors);
    this.isValid = inspection.isValid;
    this.lastUpdatedBy = inspection.lastUpdatedBy;
    this.lastUpdateDate = inspection.lastUpdateDate;
  }
}

export class InspectionSchemaLocationUpdate {
  id: string;
  inspectionLocations: string;
  lastUpdateDate: string;
  lastUpdatedBy: string;

  setInspectionLocation(inspection: InspectionUpdateLocation, userId: string) {
    const date = new FormatDate();
    this.id = inspection.inspectionId;
    this.inspectionLocations = JSON.stringify(inspection.inspectionLocations);
    this.lastUpdatedBy = userId;
    this.lastUpdateDate = date.toString();
  }
}

export class InspectionSchemaStatusUpdate {
  id: string;
  status: string;
  lastUpdateDate: string;
  lastUpdatedBy: string;

  setInspectionStatus(inspection: InspectionSchemaStatusUpdate, userId: string) {
    const date = new FormatDate();
    this.id = inspection.id;
    this.status = JSON.stringify(inspection.status);
    this.lastUpdatedBy = userId;
    this.lastUpdateDate = date.toString();
  }
}

export class InspectionQueryOpt {
  tenantId: string;
  userId: string;
  status: string;
  subjectFilter: SubjectFilter;
  isOwn: boolean;
  sort?: { active: string; direction: string; };

  constructor(tenantId: string, userId: string, statusCode?: string, subjectFilter?: SubjectFilter, isOwn?: boolean,
    sort?: { active: string; direction: string; }) {
    this.tenantId = tenantId;
    this.userId = userId;
    this.status = statusCode;
    this.subjectFilter = subjectFilter;
    this.isOwn = isOwn;
    this.sort = sort;
  }
}
